import { useState } from "react";
import type { BoardSortHint as BoardSortHintCopy } from "./Board.types";
import { SORT_HINT_EDGE_TITLES } from "./boardSortHintCopy";
import { useBoardSlotSpot } from "./useBoardSlotSpot";
import styles from "./BoardSortHint.module.scss";

/** The hint a sorted cell shows while a pointer drag is headed there. */
export interface BoardCellSortHint {
    copy: BoardSortHintCopy;
    /** The place key is held: the slot follows the pointer, and the hint steps aside. */
    isPlacing: boolean;
    /** The dragged card's placement key: the hint shows in its slot. */
    slotKey: string;
}

interface BoardSortHintProps {
    hint: BoardCellSortHint;
}

/**
 * In the slot a sorted pointer drag opens: says why the card will land
 * where the order puts it. While the slot is scrolled out of the column's
 * view, it pins to that edge and says which way the card lands. Screen
 * readers skip it: the live region already says where the card would land.
 */
export const BoardSortHint = ({
    hint: { copy, isPlacing, slotKey },
}: Readonly<BoardSortHintProps>) => {
    const [layer, setLayer] = useState<HTMLDivElement | null>(null);
    const spot = useBoardSlotSpot(layer, slotKey);
    return (
        <div
            ref={setLayer}
            aria-hidden
            className={styles.layer}
            data-placing={isPlacing}
            data-edge={spot?.edge ?? "none"}
        >
            {spot !== null && (
                <>
                    <div style={{ height: spot.middle }} />
                    <div className={styles.anchor}>
                        <p className={styles.hint}>
                            <span className={styles.title}>
                                {spot.edge === null
                                    ? copy.title
                                    : SORT_HINT_EDGE_TITLES[spot.edge]}
                            </span>
                            {copy.placeLabel !== undefined && (
                                <span className={styles.placeLabel}>
                                    {copy.placeLabel}
                                </span>
                            )}
                        </p>
                    </div>
                </>
            )}
        </div>
    );
};
